import {useEffect, useRef, useState} from "react";
import moment from "moment";
import {useParams} from "react-router-dom";
import Tab from "react-bootstrap/Tab";
import Tabs from "react-bootstrap/Tabs";
// -
import axiApi from "ApiCaller";
import {SwalAlertClass} from "core/swalClass";
import LoaderSpinner from "context/LoaderSpinner";
import TabPreviewOverflowParams from "./TabPreviewOverflowParams";
import TabPreviewForecastTable from "./TabPreviewForecastTable";
import TabPreviewTableAssetsIn from "./TabPreviewTableAssetsIn";
import TabPreviewTableAssetsOut from "./TabPreviewTableAssetsOut";
import TabPreviewMapTravel from "./TabPreviewMapTravel";
import CreateRouteOverflow from "./CreateRouteOverflow";
import {isEvaluated} from "./ModelsDynamic";

/*
 * Vista previa de la plantilla con el punto 5 (C1/C2/C3). La decisión la calcula el servidor con la misma
 * lógica que la ruta final; aquí solo se muestra. La ruta de hoy se calcula con Azure sobre los contenedores
 * a recoger, acumulando los tiempos como en la vista previa anterior.
 */
const TabPreviewOverflow = ({formData, setFormData, dynamicAssets, assets, steps, pointsModels, onSubmit, programmings}) => {
    const Alert = new SwalAlertClass({confirmButtonText: "Aceptar"});
    const {id} = useParams();
    const templateId = id !== "add" ? id : null;
    const [keyTab, setKeyTab] = useState("forecast");
    const [isLoading, setIsLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [totals, setTotals] = useState(null);
    const [assetsIn, setAssetsIn] = useState([]);
    const [assetsOut, setAssetsOut] = useState([]);
    const [routeMinutes, setRouteMinutes] = useState(null);
    const [showModalMap, setShowModalMap] = useState(false);
    const travel = useRef(null);
    const forecastRequest = useRef(0);

    // Se recalcula al pasar desde Criterios ("Previa" o al editar una plantilla guardada)
    useEffect(() => {
        if (steps.classifyAssets && assets.length) {
            fetchForecast();
        }
    }, [steps.classifyAssets]);

    const resetRoute = () => {
        travel.current = null;
        setRouteMinutes(null);
    };

    // Solo se evalúan los seleccionados en el filtro: activos y con el contenedor en estado activo.
    // params: parámetros de ruta (puntos, tiempos, duración) y criterios por defecto de la plantilla;
    // dynamic_assets: criterios de cada contenedor de la pestaña Criterios.
    const previewBody = (selected) => ({
        asset_ids: selected.map((asset) => asset.id),
        priorities: formData.priorities,
        params: {...formData.params},
        dynamic_assets: dynamicAssets.current.map((dyn) => ({
            id: dyn.asset_id,
            c1: dyn.c1,
            c2: dyn.c2,
            c3: dyn.c3,
            force: dyn.force,
            days: dyn.days,
        })),
        programming: formData.programming,
        static_route: formData.filters.routes[0]?.id ?? null, // da el orden si no se optimiza
    });

    const downloadExcel = () => {
        setIsLoading(true);
        axiApi
            .post("/api/distribution/dynamic/overflow/preview/download/", previewBody(assets.filter(isEvaluated)), {
                responseType: "blob",
            })
            .then((resp) => {
                const href = URL.createObjectURL(resp.data);
                const link = document.createElement("a");
                link.href = href;
                link.setAttribute("download", `prevision_${moment().format("DD-MM-YYYY")}.xlsx`);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(href);
            })
            .catch((error) => {
                console.error("Error al descargar el Excel:", error);
                Alert.error("Error en el servidor", "No se ha podido descargar el Excel");
            })
            .finally(() => setIsLoading(false));
    };

    const fetchForecast = () => {
        const request = ++forecastRequest.current;
        setIsLoading(true);
        const selected = assets.filter(isEvaluated);
        const assetsById = new Map(selected.map((asset) => [asset.id, asset]));
        axiApi
            .post("/api/distribution/dynamic/overflow/preview/", previewBody(selected))
            .then((resp) => {
                if (request !== forecastRequest.current) return; // hay una petición más reciente
                const items_ = resp.data.items.map((item) => ({
                    ...item,
                    asset: assetsById.get(item.id),
                    accepted: item.collect,
                    type: "asset",
                    time: 0,
                    step: 0,
                }));
                setItems(items_);
                setTotals(resp.data.totals);
                // fuera de la ruta (sin coordenadas o límite de puntos) van a "Se pueden esperar" con su motivo
                setAssetsIn(items_.filter((item) => item.collect && !item.route_out));
                setAssetsOut(
                    items_
                        .filter((item) => !item.collect || item.route_out)
                        .map((item) => ({...item, reason: item.route_out || item.reason}))
                );
                resetRoute();
            })
            .catch((error) => {
                console.error("Error al calcular la previsión:", error);
                Alert.error("Error en el servidor", "No se ha podido calcular la previsión");
            })
            .finally(() => setIsLoading(false));
    };

    // Ruta calculada en el servidor con el mismo motor que la ruta programada: los aptos tal cual, en el orden
    // de Azure, sin ajustar a la duración (solo salen los sin coordenadas y los de menor llenado si no caben).
    const calculateRoute = () => {
        if (!items.length) {
            Alert.warning("Sin previsión", "Calcula primero la previsión");
            return;
        }
        const out = items.filter((item) => item.collect && item.route_out);
        if (out.length) {
            Alert.warning(
                "Contenedores fuera de la ruta",
                `Los aptos ocupan más puntos de los que admite la ruta (${totals.points_available}) o no tienen ` +
                    `coordenadas: ${out.length} contenedores quedan fuera (los de menor llenado o sin coordenadas).`
            ).then(fetchRoute);
            return;
        }
        fetchRoute();
    };

    const fetchRoute = () => {
        setIsLoading(true);
        const itemsById = new Map(items.map((item) => [item.id, item]));
        axiApi
            .post("/api/distribution/dynamic/overflow/preview/route/", previewBody(assets.filter(isEvaluated)))
            .then((resp) => {
                const route = resp.data.route;
                const routed = route.stops.map((stop) => ({
                    ...itemsById.get(stop.id),
                    ...stop,
                    asset: itemsById.get(stop.id)?.asset,
                    accepted: itemsById.get(stop.id)?.collect,
                }));
                const routedIds = new Set(route.stops.map((stop) => stop.id));
                const removed = new Map(route.removed.map((stop) => [stop.id, stop.reason]));
                const out = items
                    .filter((item) => !routedIds.has(item.id))
                    .map((item) => ({...item, accepted: item.collect, reason: removed.get(item.id) || item.reason}));
                travel.current = route.geojson;
                setAssetsIn(routed);
                setAssetsOut(out);
                setRouteMinutes(route.minutes);
            })
            .catch((error) => {
                console.error("Error al calcular la ruta:", error);
                Alert.error("Error en el cálculo de ruta.", error.response?.data?.message || error.message);
            })
            .finally(() => setIsLoading(false));
    };

    return (
        <div className="container-fluid">
            <TabPreviewOverflowParams
                formData={formData}
                setFormData={setFormData}
                totals={totals}
                routeMinutes={routeMinutes}
                onForecast={fetchForecast}
                onDownload={downloadExcel}
                onSubmit={onSubmit}
                programmings={programmings}
            />
            {totals && (
                <div
                    className="text-muted small mb-2"
                    title="El nivel cambia con cada cálculo; el modelo es el del día operativo">
                    Calculado a las {moment(totals.calculated_at).format("HH:mm")} · medidas hasta{" "}
                    {totals.measures_until ? moment(totals.measures_until).format("DD-MM-YYYY HH:mm") : "-"} · modelo del{" "}
                    {moment(totals.date).format("DD-MM-YYYY")}
                </div>
            )}
            <LoaderSpinner isLoading={isLoading}>
                <Tabs activeKey={keyTab} id="preview-overflow-tabs" onSelect={(k) => setKeyTab(k)} className="mb-3">
                    <Tab eventKey="forecast" title="Previsiones">
                        <TabPreviewForecastTable
                            items={items}
                            days={totals?.days || []}
                            routeDays={totals?.route_days || []}
                        />
                    </Tab>
                    <Tab eventKey="route" title="Ruta">
                        <div className="row">
                            <div className="col-md-5 col-12">
                                <h5
                                    className="text-center"
                                    title="Contenedores / Paradas (una isla es un punto)">
                                    A recoger:{" "}
                                    {totals && totals.points !== assetsIn.length
                                        ? `${assetsIn.length}/${totals.points}`
                                        : assetsIn.length}
                                </h5>
                                <TabPreviewTableAssetsIn
                                    assetsIn={assetsIn}
                                    points={pointsModels.current}
                                    formData={formData}
                                />
                            </div>
                            <div className="col-md-2 col-12 text-center pt-5">
                                <button className="btn btn-outline-secondary d-block m-auto mb-3" onClick={calculateRoute}>
                                    Crear ruta
                                </button>
                                <button
                                    className="btn btn-outline-secondary d-block m-auto mb-3"
                                    onClick={() => setShowModalMap(true)}
                                    disabled={travel.current === null}>
                                    Ver ruta
                                </button>
                                <CreateRouteOverflow
                                    templateId={templateId}
                                    getBody={() => previewBody(assets.filter(isEvaluated))}
                                />
                            </div>
                            <div className="col-md-5 col-12">
                                <h5 className="text-center">Se pueden esperar: {assetsOut.length}</h5>
                                <TabPreviewTableAssetsOut assetsOut={assetsOut} />
                            </div>
                        </div>
                    </Tab>
                </Tabs>
            </LoaderSpinner>
            {showModalMap && (
                <TabPreviewMapTravel
                    show={showModalMap}
                    setShow={setShowModalMap}
                    assetsIn={assetsIn}
                    assetsOut={assetsOut}
                    travel={travel}
                    pointsModels={pointsModels}
                />
            )}
        </div>
    );
};

export default TabPreviewOverflow;
